import React, { useState, useEffect } from "react";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
// core components / custom
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import CardFooter from "components/Card/CardFooter.js";
import CustomTabs from "components/CustomTabs/CustomTabs.js";
import ReactTable from "components/ReactTable/ReactTable";
import ToggleSwitch from "components/ToggleSwitch/ToggleSwitch";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { Edit } from "@mui/icons-material";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
} from "@material-ui/core";

const styles = {
  cardCategoryWhite: {
    color: "rgba(255,255,255,.62)",
    margin: "0",
    fontSize: "12px",
    textTransform: "uppercase",
    marginTop: "0",
    marginBottom: "0",
    marginLeft: "20px",
    fontWeight: "300",
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "5px",
    minHeight: "auto",
    fontSize: "16px",
    fontWeight: "300",
    fontFamily: "'Poppins', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    marginLeft: "20px",
    textDecoration: "none",
    textTransform: "uppercase",
  },
  // NEW: spacing between fields
  fieldSpacing: {
    marginBottom: 16, // vertical spacing between inputs
    paddingLeft: 8,
    paddingRight: 8,
  },
  gridRowSpacing: {
    marginBottom: 12, // spacing between rows
  },
  buttonProgress: {
    marginLeft: 8,
  },
  toggleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
  },
  toggleWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: '80px',
  },
  toggleLabel: {
    fontSize: '11px',
    marginTop: '4px',
    color: '#666',
    fontWeight: 500,
  }
};

const useStyles = makeStyles(styles);

/**
 * Schools management page (with spacing and calendar locale tips)
 * - 3 inputs per row for the Add form
 */
export default function TypographyPage() {
  const classes = useStyles();

  // Form state (Add / Edit)
  const [schoolId, setSchoolId] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [schoolWebsite, setSchoolWebsite] = useState("");
  const [district, setDistrict] = useState("");
  const [city, setCity] = useState("");
  const [stateField, setStateField] = useState("");
  const [country, setCountry] = useState("");
  const [numSubscriptions, setNumSubscriptions] = useState(0);

  const [allOrganizations, setAllOrganizations] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isTogglingEOP, setIsTogglingEOP] = useState(false);
  const [isTogglingLegacyApp, setIsTogglingLegacyApp] = useState(false);

  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  })();

  const handleOnChangeEOPStatus = async (school_id) => {
    if (!currentUser || new Date() > new Date(currentUser.expiresInTime)) {
      toast.error("Unauthorized user. Please log in", {
        position: toast.POSITION.TOP_CENTER,
      });
      localStorage.removeItem("user");
      window.location.href = "/";
      return;
    }

    setIsTogglingEOP(school_id);
    const headers = {
      "Content-Type": "application/json",
      Authorization: "Bearer " + currentUser.accessToken,
    };

    try {
      const response = await axios.patch(
        process.env.REACT_APP_API_URL + "schools/" + school_id + "/access",
        {},
        { headers }
      );
      if (response.data && response.data.code === 200) {
        // Rafraîchir toutes les données depuis le serveur
        await getAllOrganizations();
      } else {
        toast.error(
          response.data?.message || "Failed to update organization status",
          { position: toast.POSITION.TOP_CENTER }
        );
      }
    } catch (err) {
      console.error("Toggle EOP access error:", err);
      toast.error("Failed to update organization", {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setIsTogglingEOP(false);
    }
  };

  const handleOnChangeLegacyAppStatus = async (school_id) => {
    if (!currentUser || new Date() > new Date(currentUser.expiresInTime)) {
      toast.error("Unauthorized user. Please log in", {
        position: toast.POSITION.TOP_CENTER,
      });
      localStorage.removeItem("user");
      window.location.href = "/";
      return;
    }

    setIsTogglingLegacyApp(school_id);
    const headers = {
      "Content-Type": "application/json",
      Authorization: "Bearer " + currentUser.accessToken,
    };

    try {
      const response = await axios.patch(
        process.env.REACT_APP_API_URL + "schools-access_legacy/" + school_id,
        {},
        { headers }
      );
      if (response.data && response.data.code === 200) {
        // Rafraîchir toutes les données depuis le serveur
        await getAllOrganizations();
      } else {
        toast.error(
          response.data?.message || "Failed to update legacy app status",
          { position: toast.POSITION.TOP_CENTER }
        );
      }
    } catch (err) {
      console.error("Toggle legacy app access error:", err);
      toast.error("Failed to update organization", {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setIsTogglingLegacyApp(false);
    }
  };

  const getLegacyAppStatus = (rowData) => {
    // Si access_to_legacy_app est undefined, null, empty string ou false, retourne false
    const legacyAccess = rowData.access_to_legacy_app;
    if (legacyAccess === undefined || 
        legacyAccess === null || 
        legacyAccess === "" || 
        legacyAccess === false) {
      return false;
    }
    // Si c'est "true" (string) ou true (boolean), retourne true
    return legacyAccess === true || legacyAccess === "true";
  };

  const columns = React.useMemo(
    () => [
      { Header: "School ID", accessor: "school_id" },
      { Header: "Name", accessor: "name" },
      { Header: "Website", accessor: "website" },
      { Header: "city", accessor: "city" },
      { Header: "state", accessor: "state" },
      { Header: "country", accessor: "country" },
      { Header: "num_subscriptions", accessor: "num_subscriptions" },
      { 
        Header: "Access Controls", 
        accessor: "access_controls",
        Cell: ({ row }) => (
          <div className={classes.toggleContainer}>
            {/* EOP Toggle */}
            <div className={classes.toggleWrapper}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <ToggleSwitch
                  value={row.original.school_id}
                  status={row.original.access_to_eop}
                  handleChange={() => handleOnChangeEOPStatus(row.original.school_id)}
                />
                {isTogglingEOP === row.original.school_id && (
                  <CircularProgress size={16} style={{ marginLeft: 6 }} />
                )}
              </div>
              <div className={classes.toggleLabel}>EOP</div>
            </div>
            
            {/* Legacy App Toggle */}
            <div className={classes.toggleWrapper}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <ToggleSwitch
                  value={row.original.school_id}
                  status={getLegacyAppStatus(row.original)}
                  handleChange={() => handleOnChangeLegacyAppStatus(row.original.school_id)}
                />
                {isTogglingLegacyApp === row.original.school_id && (
                  <CircularProgress size={16} style={{ marginLeft: 6 }} />
                )}
              </div>
              <div className={classes.toggleLabel}>Legacy App</div>
            </div>
          </div>
        )
      },
      { 
        Header: "Edit", 
        accessor: "edit",
        Cell: ({ row }) => (
          <IconButton
            color="primary"
            aria-label="edit"
            onClick={() => {
              setOpenModal(true);
              setSchoolId(row.original.school_id || "");
              setSchoolName(row.original.name || "");
              setSchoolWebsite(row.original.website || "");
              setDistrict(row.original.district || "");
              setCity(row.original.city || "");
              setStateField(row.original.state || "");
              setCountry(row.original.country || "");
              setNumSubscriptions(Number(row.original.num_subscriptions) || 0);
            }}
          >
            <Edit />
          </IconButton>
        )
      },
    ],
    [isTogglingEOP, isTogglingLegacyApp]
  );

  function resetForm() {
    setSchoolId("");
    setSchoolName("");
    setSchoolWebsite("");
    setDistrict("");
    setCity("");
    setStateField("");
    setCountry("");
    setNumSubscriptions(0);
  }

  const handleAddNewOrganization = async (e) => {
    e.preventDefault();
    if (!currentUser || new Date() > new Date(currentUser.expiresInTime)) {
      toast.error("Unauthorized user. Please log in", {
        position: toast.POSITION.TOP_CENTER,
      });
      localStorage.removeItem("user");
      window.location.href = "/";
      return;
    }

    if (schoolId.trim() === "" || schoolName.trim() === "") {
      toast.error("School ID and School Name are required", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    setIsAdding(true);
    const headers = {
      "Content-Type": "application/json",
      Authorization: "Bearer " + currentUser.accessToken,
    };

    try {
      const payload = {
        school_id: schoolId,
        name: schoolName,
        website: schoolWebsite,
        district,
        city,
        state: stateField,
        country,
        num_subscriptions: numSubscriptions,
      };

      const response = await axios.post(
        process.env.REACT_APP_API_URL + "schools",
        payload,
        { headers }
      );
      if (response.data && response.data.code === 201) {
        toast.success(response.data.message || "School saved successfully", {
          position: toast.POSITION.TOP_CENTER,
        });
        await getAllOrganizations();
        resetForm(); // Clear le formulaire après ajout réussi
      } else {
        toast.error(response.data?.message || "Failed to add organization", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } catch (err) {
      console.error("Add organization error:", err);
      toast.error("Failed to add organization", {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleUpdateOrganization = async (e) => {
    e.preventDefault();
    if (!currentUser || new Date() > new Date(currentUser.expiresInTime)) {
      toast.error("Unauthorized user. Please log in", {
        position: toast.POSITION.TOP_CENTER,
      });
      localStorage.removeItem("user");
      window.location.href = "/";
      return;
    }

    if (schoolId.trim() === "" || schoolName.trim() === "") {
      toast.error("School ID and School Name are required", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    setIsUpdating(true);
    const headers = {
      "Content-Type": "application/json",
      Authorization: "Bearer " + currentUser.accessToken,
    };

    try {
      const payload = {
        school_id: schoolId,
        name: schoolName,
        website: schoolWebsite,
        district,
        city,
        state: stateField,
        country,
        num_subscriptions: numSubscriptions,
      };

      const response = await axios.put(
        process.env.REACT_APP_API_URL + "schools",
        payload,
        { headers }
      );
      
      if (response.data && (response.data.code === 201 || response.data.code === 200)) {
        toast.success(response.data.message || "School updated successfully", {
          position: toast.POSITION.TOP_CENTER,
        });
        
        // Rafraîchir toutes les données depuis le serveur
        await getAllOrganizations();
        
        resetForm();
        setOpenModal(false);
      } else {
        toast.error(response.data?.message || "Failed to update organization", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } catch (err) {
      console.error("Update organization error:", err);
      toast.error("Failed to update organization", {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const getAllOrganizations = async () => {
    if (!currentUser || new Date() > new Date(currentUser.expiresInTime)) {
      toast.error("Unauthorized user. Please log in", {
        position: toast.POSITION.TOP_CENTER,
      });
      localStorage.removeItem("user");
      window.location.href = "/";
      return;
    }

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: "Bearer " + currentUser.accessToken,
      };

      const res = await fetch(process.env.REACT_APP_API_URL + "schools", {
        headers,
      });
      const data = await res.json();

      // Server returns merged DB+JSON under data (meta included)
      if (data && data.code === 200 && Array.isArray(data.data)) {
        const fetched = data.data;
        setAllOrganizations(fetched);
      } else {
        setAllOrganizations([]);
      }
    } catch (err) {
      console.error("Fetch schools error:", err);
      setAllOrganizations([]);
    }
  };

  useEffect(() => {
    getAllOrganizations();
  }, []);

  return (
    <div>
      {currentUser?.role === "Super Admin" ? (
        <div>
          <GridContainer>
            <GridItem xs={12} sm={12} md={12}>
              <CustomTabs
                title=""
                headerColor="primary"
                tabs={[
                  {
                    tabName: "Add Organization",
                    tabContent: (
                      <Card>
                        <CardHeader color="primary">
                          <h4 className={classes.cardTitleWhite}>
                            Organization Profile
                          </h4>
                          <p className={classes.cardCategoryWhite}>
                            Complete organization profile
                          </p>
                        </CardHeader>

                        <form onSubmit={handleAddNewOrganization}>
                          <CardBody>
                            {/* Row 1 (3 inputs) */}
                            <GridContainer className={classes.gridRowSpacing}>
                              <GridItem
                                xs={12}
                                sm={12}
                                md={3}
                                className={classes.fieldSpacing}
                              >
                                <CustomInput
                                  labelText="School ID"
                                  formControlProps={{ fullWidth: true }}
                                  inputProps={{
                                    value: schoolId,
                                    onChange: (e) =>
                                      setSchoolId(e.target.value),
                                  }}
                                />
                              </GridItem>
                              <GridItem
                                xs={12}
                                sm={12}
                                md={1}
                                className={classes.fieldSpacing}
                              ></GridItem>
                              <GridItem
                                xs={12}
                                sm={12}
                                md={3}
                                className={classes.fieldSpacing}
                              >
                                <CustomInput
                                  labelText="School Name"
                                  formControlProps={{ fullWidth: true }}
                                  inputProps={{
                                    value: schoolName,
                                    onChange: (e) =>
                                      setSchoolName(e.target.value),
                                  }}
                                />
                              </GridItem>
                              <GridItem
                                xs={12}
                                sm={12}
                                md={1}
                                className={classes.fieldSpacing}
                              ></GridItem>

                              <GridItem
                                xs={12}
                                sm={12}
                                md={3}
                                className={classes.fieldSpacing}
                              >
                                <CustomInput
                                  labelText="School Website"
                                  formControlProps={{ fullWidth: true }}
                                  inputProps={{
                                    value: schoolWebsite,
                                    onChange: (e) =>
                                      setSchoolWebsite(e.target.value),
                                  }}
                                />
                              </GridItem>
                            </GridContainer>

                            {/* Row 2 (3 inputs) */}
                            <GridContainer className={classes.gridRowSpacing}>
                              <GridItem
                                xs={12}
                                sm={12}
                                md={3}
                                className={classes.fieldSpacing}
                              >
                                <CustomInput
                                  labelText="City"
                                  formControlProps={{ fullWidth: true }}
                                  inputProps={{
                                    value: city,
                                    onChange: (e) => setCity(e.target.value),
                                  }}
                                />
                              </GridItem>
                              <GridItem
                                xs={12}
                                sm={12}
                                md={1}
                                className={classes.fieldSpacing}
                              ></GridItem>

                              <GridItem
                                xs={12}
                                sm={12}
                                md={3}
                                className={classes.fieldSpacing}
                              >
                                <CustomInput
                                  labelText="State"
                                  formControlProps={{ fullWidth: true }}
                                  inputProps={{
                                    value: stateField,
                                    onChange: (e) =>
                                      setStateField(e.target.value),
                                  }}
                                />
                              </GridItem>
                              <GridItem
                                xs={12}
                                sm={12}
                                md={1}
                                className={classes.fieldSpacing}
                              ></GridItem>

                              <GridItem
                                xs={12}
                                sm={12}
                                md={3}
                                className={classes.fieldSpacing}
                              >
                                <CustomInput
                                  labelText="Country"
                                  formControlProps={{ fullWidth: true }}
                                  inputProps={{
                                    value: country,
                                    onChange: (e) => setCountry(e.target.value),
                                  }}
                                />
                              </GridItem>
                            </GridContainer>

                            {/* Row 3 (3 inputs) */}
                            <GridContainer className={classes.gridRowSpacing}>
                              <GridItem
                                xs={12}
                                sm={12}
                                md={3}
                                className={classes.fieldSpacing}
                              >
                                <CustomInput
                                  labelText="Number of Subscriptions"
                                  formControlProps={{ fullWidth: true }}
                                  inputProps={{
                                    type: "number",
                                    min: 0,
                                    value: numSubscriptions,
                                    onChange: (e) =>
                                      setNumSubscriptions(Number(e.target.value)),
                                  }}
                                />
                              </GridItem>

                              <GridItem
                                xs={12}
                                sm={12}
                                md={4}
                                className={classes.fieldSpacing}
                              />
                              <GridItem
                                xs={12}
                                sm={12}
                                md={4}
                                className={classes.fieldSpacing}
                              />
                            </GridContainer>
                          </CardBody>

                          <CardFooter>
                            <Button 
                              type="submit" 
                              color="secondary"
                              disabled={isAdding}
                            >
                              {isAdding ? (
                                <>
                                  <CircularProgress 
                                    size={20} 
                                    color="inherit" 
                                    className={classes.buttonProgress}
                                  />
                                  Adding...
                                </>
                              ) : (
                                "Add Organization"
                              )}
                            </Button>
                          </CardFooter>
                        </form>

                        <ToastContainer />
                      </Card>
                    ),
                  },
                  {
                    tabName: "All Organizations",
                    tabContent: (
                      <div>
                        <div className={classes.searchWrapper}>
                          <ReactTable
                            columns={columns}
                            data={allOrganizations}
                          />
                        </div>

                        <Dialog
                          open={openModal}
                          onClose={() => {
                            setOpenModal(false);
                            resetForm();
                          }}
                          fullWidth
                          maxWidth="md"
                        >
                          <DialogTitle>Update Organization</DialogTitle>
                          <form onSubmit={handleUpdateOrganization}>
                            <DialogContent>
                              {/* Row 1 */}
                              <GridContainer className={classes.gridRowSpacing}>
                                <GridItem
                                  xs={12}
                                  sm={12}
                                  md={3}
                                  className={classes.fieldSpacing}
                                >
                                  <CustomInput
                                    labelText="School ID (read only)"
                                    formControlProps={{ fullWidth: true }}
                                    inputProps={{
                                      value: schoolId,
                                      readOnly: true,
                                    }}
                                  />
                                </GridItem>
                              <GridItem
                                xs={12}
                                sm={12}
                                md={1}
                                className={classes.fieldSpacing}
                              ></GridItem>
                                <GridItem
                                  xs={12}
                                  sm={12}
                                  md={3}
                                  className={classes.fieldSpacing}
                                >
                                  <CustomInput
                                    labelText="School Name"
                                    formControlProps={{ fullWidth: true }}
                                    inputProps={{
                                      value: schoolName,
                                      onChange: (e) =>
                                        setSchoolName(e.target.value),
                                    }}
                                  />
                                </GridItem>
                              <GridItem
                                xs={12}
                                sm={12}
                                md={1}
                                className={classes.fieldSpacing}
                              ></GridItem>
                                <GridItem
                                  xs={12}
                                  sm={12}
                                  md={3}
                                  className={classes.fieldSpacing}
                                >
                                  <CustomInput
                                    labelText="School Website"
                                    formControlProps={{ fullWidth: true }}
                                    inputProps={{
                                      value: schoolWebsite,
                                      onChange: (e) =>
                                        setSchoolWebsite(e.target.value),
                                    }}
                                  />
                                </GridItem>
                              </GridContainer>

                              {/* Row 2 */}
                              <GridContainer className={classes.gridRowSpacing}>
                                <GridItem
                                  xs={12}
                                  sm={12}
                                  md={3}
                                  className={classes.fieldSpacing}
                                >
                                  <CustomInput
                                    labelText="City"
                                    formControlProps={{ fullWidth: true }}
                                    inputProps={{
                                      value: city,
                                      onChange: (e) => setCity(e.target.value),
                                    }}
                                  />
                                </GridItem>
                              <GridItem
                                xs={12}
                                sm={12}
                                md={1}
                                className={classes.fieldSpacing}
                              ></GridItem>
                                <GridItem
                                  xs={12}
                                  sm={12}
                                  md={3}
                                  className={classes.fieldSpacing}
                                >
                                  <CustomInput
                                    labelText="State"
                                    formControlProps={{ fullWidth: true }}
                                    inputProps={{
                                      value: stateField,
                                      onChange: (e) =>
                                        setStateField(e.target.value),
                                    }}
                                  />
                                </GridItem>
                                                              <GridItem
                                xs={12}
                                sm={12}
                                md={1}
                                className={classes.fieldSpacing}
                              ></GridItem>
                                <GridItem
                                  xs={12}
                                  sm={12}
                                  md={3}
                                  className={classes.fieldSpacing}
                                >
                                  <CustomInput
                                    labelText="Country"
                                    formControlProps={{ fullWidth: true }}
                                    inputProps={{
                                      value: country,
                                      onChange: (e) =>
                                        setCountry(e.target.value),
                                    }}
                                  />
                                </GridItem>
                              </GridContainer>

                              {/* Row 3 */}
                              <GridContainer className={classes.gridRowSpacing}>
                                <GridItem
                                  xs={12}
                                  sm={12}
                                  md={3}
                                  className={classes.fieldSpacing}
                                >
                                  <CustomInput
                                    labelText="Number of Subscriptions"
                                    formControlProps={{ fullWidth: true }}
                                    inputProps={{
                                      type: "number",
                                      min: 0,
                                      value: numSubscriptions,
                                      onChange: (e) =>
                                        setNumSubscriptions(Number(e.target.value)),
                                    }}
                                  />
                                </GridItem>

                                <GridItem
                                  xs={12}
                                  sm={12}
                                  md={3}
                                  className={classes.fieldSpacing}
                                />
                                <GridItem
                                  xs={12}
                                  sm={12}
                                  md={3}
                                  className={classes.fieldSpacing}
                                />
                              </GridContainer>
                            </DialogContent>

                            <DialogActions>
                              <Button
                                onClick={() => {
                                  setOpenModal(false);
                                  resetForm();
                                }}
                                color="secondary"
                                disabled={isUpdating}
                              >
                                Cancel
                              </Button>
                              <Button 
                                type="submit" 
                                color="primary"
                                disabled={isUpdating}
                              >
                                {isUpdating ? (
                                  <>
                                    <CircularProgress 
                                      size={20} 
                                      color="inherit" 
                                      className={classes.buttonProgress}
                                    />
                                    Updating...
                                  </>
                                ) : (
                                  "Update"
                                )}
                              </Button>
                            </DialogActions>
                          </form>
                        </Dialog>
                      </div>
                    ),
                  },
                ]}
              />
            </GridItem>
          </GridContainer>
        </div>
      ) : (
        <h1 style={{ textAlign: "center" }}>
          Unauthorized page. Please contact admin
        </h1>
      )}
    </div>
  );
}